/**
 * Scroll word steps.
 *
 * Loaded by sections/scrolltrigger-word-steps.liquid with
 *   <script type="module" src="{{ 'scrolltrigger-word-steps.js' | asset_url }}"></script>
 * ES modules are deduplicated by URL, so several instances of the section on one page still fetch
 * and execute this exactly once.
 *
 * Never append custom code to assets/theme.js — a Prestige update overwrites it.
 *
 * ---------------------------------------------------------------------------------------------
 * How the effect is split between CSS and JS
 * ---------------------------------------------------------------------------------------------
 * The PIN is pure CSS: .sws__pin is `position: sticky`, which is exactly "scroll normally until you
 * reach the top of the screen, then stay put". No JS pins anything, so there is no injected spacer
 * and nothing to refresh when the theme editor re-renders the section. (Same reasoning as
 * scrolltrigger-collection-cards: GSAP's headline ScrollTrigger feature, pin: true, is what sticky
 * already does natively, and on Shopify it would additionally need ScrollTrigger.refresh() wired
 * into the editor's re-render cycle or the effect silently dies while the merchant judges it.)
 *
 * JS answers exactly one question per scroll frame: WHICH STEP are we on, 0 … count-1? It then
 * marks the DOM with two independent attributes and lets CSS decide what they mean:
 *
 *   data-lit      — on words 0 … step. Cumulative: "this word has been reached."
 *   data-current  — on the ONE word and the two images at `step`.
 *
 * Desktop CSS honours `data-lit` (the staircase fills in and earlier words stay lit); mobile CSS
 * honours `data-current` (one word at a time, between its two images). So the two choreographies
 * need no branching in JS at all, and no rebuild when the breakpoint is crossed.
 *
 * ---------------------------------------------------------------------------------------------
 * Why there is no smoothing loop here
 * ---------------------------------------------------------------------------------------------
 * scrolltrigger-collection-cards eases a CONTINUOUS value, so it runs a rAF glide. This section's
 * value is quantised to a step index, so easing it would buy nothing — the softness comes from CSS
 * transitions on colour and opacity instead. #paint() also returns early unless the step actually
 * changed, so a full scroll through a 3-step section touches the DOM 3 times, not once per frame.
 *
 * ---------------------------------------------------------------------------------------------
 * Visibility contract — the part that must not be broken
 * ---------------------------------------------------------------------------------------------
 * Nothing here is what makes content visible. With no script every word renders at full colour in
 * the staircase and every image renders in normal flow inside its slot; the pin, the clipping, the
 * absolute positioning and the dimming are ALL gated in CSS behind [data-active], which only this
 * file sets and only once the effect is wired up. A failure therefore degrades to a plain, readable
 * section — never a blank one, and never a long stretch of dead scroll.
 */

import { scroll } from 'vendor';
import { matchesMediaQuery } from 'theme';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

class ScrolltriggerWordSteps extends HTMLElement {
  #abortController;
  #resizeObserver = null;
  #stopScroll = null;
  #started = false;

  #pin = null;
  #words = [];
  #media = [];
  #count = 0;

  #pinHeight = 0;
  #pinTop = 0;

  // -1 so the first #paint() always applies, whatever step it resolves to.
  #step = -1;

  // The step the merchant currently has selected in the theme editor. Held on screen so they can
  // see the word they are editing instead of whichever step the scroll position happens to be on.
  #selectedStep = null;

  connectedCallback() {
    this.#abortController = new AbortController();

    // The merchant can switch the effect off; the section is then a plain static block.
    if (!this.hasAttribute('effect')) return;

    // Reduced motion: the CSS pins nothing in that case, so there is no state to undo — leave the
    // section completely static, with every word lit and every image visible.
    if (!matchesMediaQuery('motion-safe')) return;

    this.#pin = this.querySelector('.sws__pin');
    this.#words = Array.from(this.querySelectorAll('[data-sws-word]'));
    this.#media = Array.from(this.querySelectorAll('[data-sws-media]'));
    this.#count = this.#words.length;

    if (!this.#pin || this.#count === 0) return;

    const { signal } = this.#abortController;

    // Both heights are viewport-derived, so they change on rotation, window resize and mobile
    // chrome changes. Observing the host and the pin catches all of that without polling. No
    // breakpoint listener is needed: the desktop/mobile difference lives entirely in CSS.
    this.#resizeObserver = new ResizeObserver(() => {
      this.#measure();
      this.#update();
    });
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(this.#pin);

    if (window.Shopify?.designMode) {
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const word = event.target.closest('[data-sws-word]');
          this.#selectedStep = word ? Number(word.dataset.index) : null;
          this.#update();
        },
        { signal }
      );

      this.addEventListener(
        'shopify:block:deselect',
        () => {
          this.#selectedStep = null;
          this.#update();
        },
        { signal }
      );
    }

    this.#start();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#teardown();
  }

  #start() {
    if (this.#started) return;

    // Switches the CSS into its pinned form. Set BEFORE #measure(), because until the pin is
    // actually sticky its computed `top` is `auto` and parseFloat would yield NaN.
    this.setAttribute('data-active', '');

    this.#measure();
    this.#update();

    // scroll() with a bare callback fires on every scroll frame, rAF-throttled by Motion One.
    this.#stopScroll = scroll(() => this.#update());

    this.#started = true;
  }

  #teardown() {
    this.#stopScroll?.();
    this.#stopScroll = null;
    this.#started = false;
    this.#step = -1;
    this.removeAttribute('data-active');

    for (const word of this.#words) {
      word.removeAttribute('data-lit');
      word.removeAttribute('data-current');
    }

    for (const media of this.#media) {
      media.removeAttribute('data-current');
    }
  }

  #measure() {
    this.#pinHeight = this.#pin.getBoundingClientRect().height;

    // The resolved `top` of the sticky element — the offset it parks at. Currently always 0 (the
    // pin deliberately ignores the header, so no dimension moves when the header hides), but read
    // from the CSS rather than hard-coded so the maths stays correct if that ever changes.
    const pinTop = parseFloat(getComputedStyle(this.#pin).top);
    this.#pinTop = Number.isFinite(pinTop) ? pinTop : 0;
  }

  #readProgress() {
    const rect = this.getBoundingClientRect();

    // The host stays in flow and is the scroll spacer: one pin tall plus the scroll length the
    // merchant asked for. That surplus is exactly how far the page scrolls while the pin is stuck,
    // so it is the distance our 0 → 1 progress is measured over.
    const travel = rect.height - this.#pinHeight;

    return travel > 0 ? clamp((this.#pinTop - rect.top) / travel, 0, 1) : 1;
  }

  #update() {
    if (this.#selectedStep !== null) {
      this.#paint(clamp(this.#selectedStep, 0, this.#count - 1));
      return;
    }

    // Even slices: with 3 words the steps change at progress 1/3 and 2/3. floor() would return
    // `count` at exactly 1, so the clamp keeps the last step active all the way to the end.
    this.#paint(clamp(Math.floor(this.#readProgress() * this.#count), 0, this.#count - 1));
  }

  #paint(step) {
    // The step only changes a handful of times across the whole pinned stretch, so bailing here
    // keeps a full scroll down to a few DOM writes instead of one per frame.
    if (step === this.#step) return;
    this.#step = step;

    for (const word of this.#words) {
      const index = Number(word.dataset.index);

      // Cumulative — desktop reads this one.
      word.toggleAttribute('data-lit', index <= step);

      // Exactly one — mobile reads this one.
      word.toggleAttribute('data-current', index === step);
    }

    for (const media of this.#media) {
      media.toggleAttribute('data-current', Number(media.dataset.index) === step);
    }
  }
}

if (!window.customElements.get('scrolltrigger-word-steps')) {
  window.customElements.define('scrolltrigger-word-steps', ScrolltriggerWordSteps);
}
